import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Form } from "react-bootstrap";
import { BsQuote } from "react-icons/bs";
import { listHomepageReviews } from "../../features/home/components/homepageReviewApi";
import { listPublicReviewCourses } from "./publicReviewCoursesApi";
import { StudentReviewAuthor, studentReviewDate } from "../../shared/reviews/StudentReviewPresentation";
import "./PublicCourseReviews.css";

const failureText = (error) => error?.code === "functions/failed-precondition" &&
  (error?.message?.includes("reviews-not-enabled") || error?.details?.reason === "reviews-not-enabled")
  ? "รีวิวจากผู้เรียนยังไม่เปิดให้บริการในขณะนี้"
  : "โหลดรีวิวไม่สำเร็จ กรุณาลองอีกครั้ง";

// Injectable adapters keep offline previews isolated; authorization is server-side.
export default function PublicCourseReviews({ loadReviews = listHomepageReviews, loadCourses = listPublicReviewCourses }) {
  const [course, setCourse] = useState("");
  return <div className="container py-4 public-course-reviews">
    <h1 className="h3 d-flex align-items-center gap-2 mb-4"><BsQuote aria-hidden="true" focusable="false" className="flex-shrink-0" /><strong>รีวิวจากผู้เรียน</strong></h1>
    <PublicCourseFilter loadCourses={loadCourses} value={course} onChange={setCourse} />
    <PublicReviewList key={course} courseId={course || undefined} loadReviews={loadReviews} />
  </div>;
}

function PublicCourseFilter({ loadCourses, value, onChange }) {
  const [courses, setCourses] = useState([]);
  const [next, setNext] = useState(null);
  const [request, setRequest] = useState({ cursor: null, attempt: 0 });
  const [busy, setBusy] = useState(Boolean(loadCourses));
  const [error, setError] = useState(false);
  const pending = useRef(Boolean(loadCourses));
  useEffect(() => {
    if (!loadCourses) return;
    let current = true;
    pending.current = true; setBusy(true); setError(false);
    Promise.resolve().then(() => loadCourses(request.cursor)).then((result) => {
      if (!current) return;
      setCourses((previous) => [...new Map([...(request.cursor ? previous : []), ...result.courses].map((course) => [course.id, { id: course.id, name: course.name }])).values()]);
      setNext(result.nextCursor);
    }).catch(() => { if (current) setError(true); }).finally(() => {
      if (current) { pending.current = false; setBusy(false); }
    });
    return () => { current = false; };
  }, [loadCourses, request]);
  const load = (cursor) => {
    if (pending.current) return;
    pending.current = true; setBusy(true);
    setRequest((previous) => ({ cursor, attempt: previous.attempt + 1 }));
  };
  return <Form.Group controlId="public-review-course" className="mb-4">
    <Form.Label>เลือกคอร์สเรียน</Form.Label>
    <Form.Select value={value} disabled={!loadCourses || busy} onChange={(event) => onChange(event.target.value)}>
      <option value="">ทุกคอร์ส</option>
      {courses.map((course) => <option key={course.id} value={course.id}>{course.name}</option>)}
    </Form.Select>
    {!loadCourses && <p className="public-review-secondary small mt-2">ตัวเลือกคอร์สยังไม่พร้อมใช้งาน ขณะนี้ดูรีวิวรวมทุกคอร์สได้</p>}
    {busy && <div role="status">กำลังโหลดคอร์ส...</div>}
    {error && <Alert variant="danger">โหลดรายชื่อคอร์สไม่สำเร็จ <Button disabled={busy} size="sm" onClick={() => load(request.cursor)}>ลองโหลดคอร์สอีกครั้ง</Button></Alert>}
    {next && !error && <Button variant="outline-secondary" size="sm" disabled={busy} onClick={() => load(next)}>โหลดคอร์สเพิ่มเติม</Button>}
  </Form.Group>;
}

function PublicReviewList({ courseId, loadReviews }) {
  const [reviews, setReviews] = useState([]);
  const [next, setNext] = useState(null);
  const [request, setRequest] = useState({ cursor: null, attempt: 0 });
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState("");
  const [emptyWindow, setEmptyWindow] = useState(false);
  const pending = useRef(true);
  useEffect(() => {
    let current = true;
    pending.current = true; setBusy(true); setError("");
    loadReviews(courseId, request.cursor).then((result) => {
      if (!current) return;
      setReviews((previous) => [...new Map([...(request.cursor ? previous : []), ...result.reviews].map((review) => [review.reviewId, review])).values()]);
      setNext(result.nextCursor);
      setEmptyWindow(result.reviews.length === 0 && Boolean(result.nextCursor));
    }).catch((failure) => { if (current) setError(failureText(failure)); }).finally(() => {
      if (current) { pending.current = false; setBusy(false); }
    });
    return () => { current = false; };
  }, [courseId, loadReviews, request]);
  const load = (cursor) => {
    if (pending.current) return;
    pending.current = true; setBusy(true);
    setRequest((previous) => ({ cursor, attempt: previous.attempt + 1 }));
  };
  return <section aria-label="รายการรีวิว" aria-busy={busy}>
    <div className="public-review-list">
      {reviews.map((review) => <article className="public-review-list-item" key={review.reviewId}>
        <StudentReviewAuthor review={review} maskPublicName />
        <div className="public-review-secondary small mt-2">{studentReviewDate(review.createdAt)}</div>
        <p className="public-review-list-text my-3">{review.reviewText}</p>
        <div className="public-review-secondary small public-review-course-name">{review.courseName}</div>
      </article>)}
    </div>
    {busy && <div role="status" className="my-3">กำลังโหลดรีวิว...</div>}
    {error && <Alert variant="danger" className="mt-3">{error} <Button disabled={busy} size="sm" onClick={() => load(request.cursor)}>ลองอีกครั้ง</Button></Alert>}
    {!busy && !error && emptyWindow && <p role="status" className="mt-3">ยังไม่พบรีวิวในช่วงนี้ กดโหลดเพิ่มเติมเพื่อดูรีวิวที่เก่ากว่า</p>}
    {!busy && !error && !reviews.length && !next && <p className="mt-3">{courseId ? "ยังไม่มีรีวิวสำหรับคอร์สนี้" : "ยังไม่มีรีวิวที่เผยแพร่"}</p>}
    {next && !error && <Button className="mt-3" disabled={busy} onClick={() => load(next)}>โหลดเพิ่มเติม</Button>}
  </section>;
}
